import { tokens } from '@fluentui/react-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsFeatureEnabled } from '../api/features/features';
import { IExecutionStatusStorage } from '../data-types/IExecutionStatusStorage';
import { GetJobStatusByKey, GetJobStatusCodeValue, JobStatusCode } from '../data-types/jobStatusCode';
import { TaxReturnStatusNotificationData, TaxReturnStatusSignalREvent } from '../data-types/SignalREvent';
import { useEventsToastMessages } from '../hooks/useEventsToastMessages';
import { useExportReports } from '../hooks/useExportReports';
import { useTaxReturnCalculationState } from '../hooks/useTaxReturnCalculationState';
import { useToastMessage } from '../hooks/useToastMessage';
import { CalculationExecutionStatusCode, TaxReturnPdfReportAvailableRequest } from '../model';
import { CommandType, SourceKeys } from '../utils/EventGrid.constants';
import { EntitySelectorInPrintModalEnabled } from '../utils/FeatureFlags.constants';
import { OnReceiveJobStatusCallbackType } from './taxReturnCalculationContext';
import { useNotification } from './useNotification';

export function TaxReturnStatusHandler(props: { children?: React.ReactNode }) {
    const { connection } = useNotification();
    const { updateTaxReturnCalculationReport, getReportStatusByJobId, taxReturnCalculationStatusList, startPolling } =
        useTaxReturnCalculationState();
    const { downloadTaxReturnPdf, printTaxReturnPdf, isTaxReturnPdfAvailable } = useExportReports();
    const { showToast } = useToastMessage();
    const { t } = useTranslation();
    const { data: isExportPrintFormsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'ExportPrintFormsEnabled',
    });
    const { data: isEntitySelectorInPrintModalEnabled } = useIsFeatureEnabled({ featureFlagName: EntitySelectorInPrintModalEnabled });
    const { checkForErrorsAndShowToast } = useEventsToastMessages();

    const onReceivePollingJobStatusCallback: OnReceiveJobStatusCallbackType = (response) => {
        const observedStatus = taxReturnCalculationStatusList?.filter((status) => response.some((res: any) => res.jobId === status.jobId));

        observedStatus?.forEach((status) => {
            const responseStatus = response.find((r: any) => r.jobId === status.jobId);

            if (status.commandType === CommandType.Print && status.statusCode === CalculationExecutionStatusCode.Executing) {
                handlePrintStatus(
                    GetJobStatusByKey(responseStatus?.statusCode || '') || JobStatusCode.Unknown,
                    status.commandType,
                    status.batchProcessId,
                    status.taxReturnItemName || ''
                );
            }
        });
    };

    useEffect(() => {
        const cancelPolling = startPolling(onReceivePollingJobStatusCallback);
        return () => cancelPolling();
    }, [taxReturnCalculationStatusList]);

    const showReadyFileToast = (batchId: string, returnName: string) => {
        showToast(
            t('returnReady'),
            <span>
                <span style={{ fontWeight: tokens.fontWeightMedium }}> {returnName}</span> {t('isReadyToPrintDownload')}
            </span>,
            'success',
            [
                {
                    linkProps: {
                        onClick: () => printTaxReturnPdf(batchId),
                    },
                    text: t('print'),
                },
                {
                    linkProps: {
                        onClick: () => downloadTaxReturnPdf(batchId, returnName),
                    },
                    text: t('download'),
                },
            ]
        );
    };

    const showNoPrintableContentToast = (returnName: string) => {
        showToast(
            t('unableToPrintDownload'),
            <span>
                <span style={{ fontWeight: tokens.fontWeightMedium }}> {returnName}</span> {t('printDownloadSelectionsProducedNoOutput')}
            </span>,
            'info'
        );
    };

    const showDefaultToast = (message: string) => {
        showToast(undefined, message, 'info');
    };

    const showDefaultErrorToast = () => {
        showToast(t('error'), t('anErrorOccurred'), 'error');
    };

    const handlePrintStatus = async (
        jobStatusCode: JobStatusCode,
        commandType: CommandType | undefined,
        batchProcessId: string | undefined,
        taxReturnItemName: string | undefined,
        eventMessage?: string
    ) => {
        if (commandType === CommandType.Print) {
            if (jobStatusCode === JobStatusCode.Unknown || jobStatusCode === JobStatusCode.Executing) {
                return;
            }

            if (
                (jobStatusCode === JobStatusCode.Completed || jobStatusCode === JobStatusCode.CompletedWithErrors) &&
                batchProcessId &&
                taxReturnItemName
            ) {
                const pdfAvailableRequest: TaxReturnPdfReportAvailableRequest = {
                    batchProcessId: batchProcessId,
                    returnName: taxReturnItemName,
                };

                if (!isEntitySelectorInPrintModalEnabled || (await isTaxReturnPdfAvailable(pdfAvailableRequest))) {
                    showReadyFileToast(batchProcessId, taxReturnItemName);
                } else {
                    showNoPrintableContentToast(taxReturnItemName);
                }

                return;
            }

            if (
                jobStatusCode === JobStatusCode.Terminated ||
                jobStatusCode === JobStatusCode.TimedOut ||
                jobStatusCode === JobStatusCode.Canceled
            ) {
                showDefaultErrorToast();
                return;
            }

            if (eventMessage) showDefaultToast(eventMessage);
        }
    };

    const handlePrintStatusFromLocalStorage = (
        jobStatusCode: JobStatusCode,
        taxReturnStatus: IExecutionStatusStorage | undefined,
        eventMessage: string
    ) => {
        handlePrintStatus(
            jobStatusCode,
            taxReturnStatus?.commandType,
            taxReturnStatus?.batchProcessId,
            taxReturnStatus?.taxReturnItemName!,
            eventMessage
        );
    };

    const handlePrintStatusFromNotificationData = (
        jobStatusCode: JobStatusCode,
        notificationData: TaxReturnStatusNotificationData,
        eventMessage: string
    ) => {
        handlePrintStatus(jobStatusCode, notificationData.CommandType, notificationData.BatchId, notificationData.Message, eventMessage);
    };

    const handleTaxReturnStatus = (
        event: TaxReturnStatusSignalREvent,
        commandType: CommandType | undefined,
        taxReturnStatus: IExecutionStatusStorage | undefined
    ) => {
        if (taxReturnStatus && commandType) {
            if (taxReturnStatus.statusCode === CalculationExecutionStatusCode.Canceled) {
                return;
            }

            const status = event.jobStatusCode === JobStatusCode.Unknown ? JobStatusCode.Executing : event.jobStatusCode;

            updateTaxReturnCalculationReport({
                ...taxReturnStatus,
                statusCode: GetJobStatusCodeValue(status) as CalculationExecutionStatusCode,
                jobId: event?.jobId,
                commandType: commandType,
            });

            checkForErrorsAndShowToast(event, taxReturnStatus);
        }
    };

    const onTaxReturnChangeHandler = (event: TaxReturnStatusSignalREvent) => {
        const notificationData = event.notificationData;
        const jobStatusCode = event.jobStatusCode;
        const taxReturnStatus = getReportStatusByJobId(event.jobId);
        const commandType = notificationData?.CommandType ?? taxReturnStatus?.commandType;

        if (isExportPrintFormsEnabled) {
            if (notificationData) {
                handlePrintStatusFromNotificationData(jobStatusCode, notificationData, event.message);
            } else {
                handlePrintStatusFromLocalStorage(jobStatusCode, taxReturnStatus, event.message);
            }
        }

        handleTaxReturnStatus(event, commandType, taxReturnStatus);
    };

    useEffect(() => {
        if (connection) {
            connection.on(SourceKeys.TaxReturn, onTaxReturnChangeHandler);
            return () => {
                connection.off(SourceKeys.TaxReturn, onTaxReturnChangeHandler);
            };
        }
    }, [connection, onTaxReturnChangeHandler]);

    return <>{props.children}</>;
}

export default TaxReturnStatusHandler;
